import { cutToast } from 'utility/toast';
import {
  useApolloClient,
  // useApolloClient,
  useMutation,
} from '@apollo/client';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import {
  TravelAuthorizationApprovalRevision,
  TravelAuthorizationApprovalRevisionVariables,
} from './__generated__/TravelAuthorizationApprovalRevision';
import {
  TravelAuthorizationDocumentPackageStatus,
  TravelAuthorizationDocumentPackageStatusVariables,
} from 'common/graphql/DocumentPackageSubscription/__generated__/TravelAuthorizationDocumentPackageStatus';
import { DocumentPackageStatusType } from 'common/types/globalTypes';
import { Stack } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import TRAVELER_AUTHORIZATION_APPROVAL_REVISION from './TravelAuthorizationApprovalRevision.graphql';
import TRAVELER_AUTHORIZATION_DOCUMENT_PACKAGE_STATUS from 'common/graphql/DocumentPackageSubscription/TravelAuthorizationDocumentPackageStatus.graphql';

const TITLE = 'Are you sure ?';
const SUBTEXT =
  'This will cancel the approved travel plan and create a revised travel plan that can be resubmitted for approval';
interface ReviseModalProps {
  travelAuthorizationData:
    | TravelAuthorization_travelAuthorization
    | null
    | undefined;
  onClose?: () => void;
  isOpen: boolean;
}

export const ReviseModal: React.FC<ReviseModalProps> = ({
  travelAuthorizationData,
  onClose,
  isOpen,
}) => {
  const client = useApolloClient();
  const history = useHistory();

  const { id, _rowTimestamp } = { ...travelAuthorizationData };

  const [createRevision] = useMutation<
    TravelAuthorizationApprovalRevision,
    TravelAuthorizationApprovalRevisionVariables
  >(TRAVELER_AUTHORIZATION_APPROVAL_REVISION, { errorPolicy: 'all' });

  const onDismiss = () => {
    onClose?.();
  };

  const onConfirm = async () => {
    const { data, errors } = await createRevision({
      variables: {
        input: {
          entityId: id!,
          rowTimestamp: _rowTimestamp!,
        },
      },
    });
    if (errors?.length) {
      cutToast.error(errors?.[0].message);
    } else {
      onClose?.();
      cutToast.success('Successfully created a Revision');
      history.replace(
        `/ta/travel-plan/travel/${data?.travelAuthorizationApprovalRevision?.revisedApprovalEntity}`
      );
      const observer = client.subscribe<
        TravelAuthorizationDocumentPackageStatus,
        TravelAuthorizationDocumentPackageStatusVariables
      >({
        query: TRAVELER_AUTHORIZATION_DOCUMENT_PACKAGE_STATUS,
        variables: {
          id: id!,
        },
      });

      const subscription = observer.subscribe(({ data, errors }) => {
        if (errors)
          cutToast.error(
            'Errors received while Subscribing to document package'
          );
        else {
          const { document, status } = {
            ...data?.travelAuthorizationDocumentPackageStatus,
          };

          if (status === 'REGENERATION_FAILURE') {
            cutToast.error(
              'Report generation failed. Document package was not created'
            );
          }
          if (status === 'FAILURE') {
            cutToast.error('Error while creating Document package ');
          }
          if (document) {
            cutToast.success('Document package created');
            client.cache.modify({
              id: client.cache.identify({
                ...travelAuthorizationData,
              }),
              fields: {
                stampedEntityDocumentId: () => {
                  return document.id;
                },
              },
            });
          }
        }
        subscription.unsubscribe();
      });
    }
  };

  return (
    <Stack>
      <ConfirmDialog
        hidden={!isOpen}
        title={TITLE}
        subText={SUBTEXT}
        onDismiss={onDismiss}
        maxWidth={400}
        onConfirm={onConfirm}
        confirmText={'Revise'}
      />
    </Stack>
  );
};
